import {
    AuthenticatedUser,
    loginSuccess,
    selectAuthenticatedUser,
    selectAuthEnvironment,
    setUser,
    useDispatch,
    useSelector
} from '@missionlabs/api'
import {
    createContext,
    FC,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react'
import { useLocation } from 'react-router-dom'
import { useSetUpDestination } from 'shared/hooks/useSetUpDestination'
import { store, useLoginWithKeycloakMutation } from 'shared/store'

import { initKeycloak, keycloak, readKeycloakUsername, startKeycloakLogin } from './keycloak'
import { establishKeycloakSession } from './keycloakSession'

type KeycloakContextValue = {
    loginWithKeycloak: (username: string) => Promise<void>
    error: string | null
}

const KeycloakContext = createContext<KeycloakContextValue | null>(null)

export function useKeycloakLogin() {
    const context = useContext(KeycloakContext)
    if (!context) {
        throw new Error('useKeycloakLogin must be used within KeycloakProvider')
    }
    return context
}

interface KeycloakProviderProps {
    children: ReactNode
}

export const KeycloakProvider: FC<KeycloakProviderProps> = ({ children }) => {
    const dispatch = useDispatch()
    const location = useLocation()
    const { environment } = useSelector(selectAuthEnvironment)
    const [loginWithKeycloakToken] = useLoginWithKeycloakMutation()
    const setUpDestination = useSetUpDestination()
    const [error, setError] = useState<string | null>(null)

    const establishSession = useCallback(
        (idToken: string) =>
            establishKeycloakSession(async () => {
                if (selectAuthenticatedUser(store.getState())) return
                const authResponse = await loginWithKeycloakToken(idToken).unwrap()
                const appUser: AuthenticatedUser = {
                    token: authResponse.ID,
                    userID: authResponse.userID,
                    clientID: authResponse.clientID,
                    username: readKeycloakUsername()
                }
                dispatch(setUser(appUser))
                await setUpDestination(appUser)
                dispatch(loginSuccess())
            }),
        [dispatch, loginWithKeycloakToken, setUpDestination]
    )

    useEffect(() => {
        if (process.env.REACT_APP_GUEST === 'true') return

        let active = true

        initKeycloak(environment)
            .then(authenticated => {
                if (!active || !authenticated || !keycloak.idToken) return
                return establishSession(keycloak.idToken)
            })
            .catch(err => {
                console.error('Failed to establish Keycloak session', err)
                if (active) setError('Multi-factor sign in failed')
            })

        return () => {
            active = false
        }
        // Re-run when the login environment changes so dev can target dev, uat, or prod.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [environment])

    const loginWithKeycloak = useCallback(
        async (username: string) => {
            const redirect = (location.state as { redirect?: string } | null)?.redirect ?? '/home'
            const idToken = await startKeycloakLogin(username, redirect, environment)
            // Electron sign-in finishes in a child window and returns the ID token.
            // The web flow navigates away and does not resolve.
            if (idToken) await establishSession(idToken)
        },
        [establishSession, environment, location.state]
    )

    const value = useMemo(() => ({ loginWithKeycloak, error }), [loginWithKeycloak, error])

    return <KeycloakContext.Provider value={value}>{children}</KeycloakContext.Provider>
}
